import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { theme } from '../../../../shared/theme';
import {
    BookingAttendee,
    RsvpConfig as ConfigData,
} from '../../../types/reservation';
import { RsvpConfig } from './RsvpConfig';
import RsvpTable from './RsvpTable';

export default function RsvpModal({
    config,
    attendees,
    open,
    onClose,
    bookingId,
    reservationStatus,
}: {
    config: ConfigData;
    attendees: BookingAttendee[];
    open: boolean;
    onClose: () => void;
    bookingId: number;
    reservationStatus: string;
}) {
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [tab, setTab] = useState<string>('config');
    const [query, setQuery] = useState('');

    const scrollRef = useRef<HTMLDivElement | null>(null);

    const {
        accepted,
        declined,
        pending,
        acceptedCount,
        declinedCount,
        pendingCount,
    } = useMemo(() => {
        const accepted = attendees.filter(
            ({ rsvp: { status } }) => status === 'accepted',
        );
        const declined = attendees.filter(
            ({ rsvp: { status } }) => status === 'declined',
        );
        const pending = attendees.filter(
            ({ rsvp: { status } }) => status === 'pending',
        );

        return {
            accepted,
            declined,
            pending,
            acceptedCount: accepted.length,
            declinedCount: declined.length,
            pendingCount: pending.length,
        };
    }, [attendees]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            return;
        }

        if (!config?.rsvpEnabled) {
            setTab('config');
            return;
        }

        if (acceptedCount > 0) {
            setTab('accepted');
            return;
        }

        if (pendingCount > 0) {
            setTab('pending');
            return;
        }

        setTab('declined');
    }, [open]);

    const matchesQuery = (row: any, q: string) => {
        const s = String(q || '')
            .trim()
            .toLowerCase();
        if (!s) return true;

        const name = `${row?.firstName ?? ''} ${row?.lastName ?? ''}`
            .trim()
            .toLowerCase();
        const email = String(row?.email ?? '')
            .trim()
            .toLowerCase();

        return name.includes(s) || email.includes(s);
    };

    const filteredAccepted = useMemo(
        () => accepted.filter((r) => matchesQuery(r, query)),
        [accepted, query],
    );

    const filteredDeclined = useMemo(
        () => declined.filter((r) => matchesQuery(r, query)),
        [declined, query],
    );

    const filteredPending = useMemo(
        () => pending.filter((r) => matchesQuery(r, query)),
        [declined, query],
    );

    const showingCount =
        tab === 'accepted'
            ? filteredAccepted.length
            : tab === 'declined'
              ? filteredDeclined.length
              : filteredPending.length;
    const totalCount =
        tab === 'accepted'
            ? accepted.length
            : tab === 'declined'
              ? declined.length
              : pending.length;

    const handleTabChange = (_: any, v: string) => {
        setTab(v);
        requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={false}>
            <DialogTitle>
                RSVP
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Box
                sx={{
                    px: isMobile ? 2 : 3,
                    pb: 2,
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    flexShrink: 0,
                    backgroundColor: theme.palette.neutral.main,
                    borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                }}
            >
                {config?.rsvpEnabled ? (
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        sx={{ flex: 1, minWidth: 280 }}
                        variant='standard'
                    >
                        <Tab
                            value='accepted'
                            label={`Accepted (${acceptedCount})`}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        />
                        <Tab
                            value='declined'
                            label={`Declined (${declinedCount})`}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        />
                        <Tab
                            value='pending'
                            label={`Pending (${pendingCount})`}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        />
                        <Tab
                            value='config'
                            label={`RSVP Config`}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        />
                    </Tabs>
                ) : (
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        sx={{ flex: 1, minWidth: 280 }}
                        variant='standard'
                    >
                        <Tab
                            value='config'
                            label={`RSVP Config`}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        />
                    </Tabs>
                )}

                {tab !== 'config' && (
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder='Search name or email'
                        size='small'
                        sx={{ width: isMobile ? '100%' : 380 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon
                                        fontSize='small'
                                        sx={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>
            <DialogContent ref={scrollRef}>
                <RsvpTable
                    bookingId={bookingId}
                    accepted={tab === 'accepted' ? filteredAccepted : []}
                    declined={tab === 'declined' ? filteredDeclined : []}
                    pending={tab === 'pending' ? filteredPending : []}
                    reservationStatus={reservationStatus}
                    isMobile={isMobile}
                />
                {tab === 'config' && (
                    <RsvpConfig
                        bookingId={bookingId}
                        config={config}
                        reservationStatus={reservationStatus}
                    />
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: isMobile ? 2 : 3,
                    py: 2,
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 800,
                    }}
                >
                    {tab === 'config'
                        ? ''
                        : `(Showing ${showingCount} of ${totalCount})`}
                </Typography>
                <Button variant='contained' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
